import {
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Stack,
    useTheme,
} from "@mui/material";
import { useState } from "react";
import ProficiencyGroupClient, { ProficiencyGroupErrors } from "../apiClients/ProficiencyGroupClient"
import { useErrorDisplay } from "../components/ErrorDisplay"
import { IsEmpty } from "../Helpers";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import EditIcon from '@mui/icons-material/Edit';
import { ProficiencyGroupType, UpsertAction } from "../Enums";
import WeightSelectControl from "../components/WeightSelectControl";
import useViewportMode from "../hooks/useViewportMode";
import { ResponsiveDialog, StickyDialogActions } from "../components/responsive";

export interface EditProficiencyGroupPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    proficiencyId: string;
    proficiencyName: string;
    proficiencyWeight: number;
    proficiencyType: ProficiencyGroupType
    onChange?: () => Promise<void>;
    icon?: JSX.Element;
}

const EditProficiencyGroupPopup = ({ proficiencyId, proficiencyName, proficiencyWeight, proficiencyType, onChange = async () => { }, icon = <EditIcon htmlColor='lightgreen' fontSize='small' />, ...btnProps }: EditProficiencyGroupPopupProps) => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const { mode: viewportMode, isDesktop } = useViewportMode();

    const [name, setName] = useState<string>(proficiencyName);
    const [type] = useState<ProficiencyGroupType>(proficiencyType);
    const [weight, setWeight] = useState<number>(proficiencyWeight);

    const disableSave = IsEmpty(name) || (name === proficiencyName && weight === proficiencyWeight);

    const crudName = `Edit Proficiency Group`;

    const handleEdit = async () => {
        try {
            setBusy(true);
            await new ProficiencyGroupClient().Update(proficiencyId, name.trim(), weight, type);
            onChange();

        } catch (error) {
            setError(error, ProficiencyGroupErrors.Update);
        }
        finally {
            setBusy(false);
            setOpen(false);
        }
    };

    const handleOpen = () => {
        setOpen(true);
    };

    return <>
        <ErrorDisplay />
        <IconButtonEx disabled={busy} hoverContent={crudName} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog aria-labelledby="customized-dialog-title" open={open} fullWidth={true}>

            <DialogTitle id="customized-dialog-title" sx={{ margin: 0, padding: theme.spacing(2) }}>
                {crudName}
            </DialogTitle>
            <DialogContent dividers sx={{ padding: theme.spacing(2), overflowY: 'auto' }} data-viewport-mode={viewportMode}>
                <BusySpinner busy={busy}>
                    <Stack spacing={2} sx={{ '& .MuiInputBase-root, & .MuiButtonBase-root': { minHeight: isDesktop ? undefined : 44 } }}>
                        <TextField label="Name" fullWidth
                            sx={{ marginBottom: theme.spacing(1) }}
                            error={IsEmpty(name)}
                            size='small'
                            variant="outlined"
                            value={name}
                            onChange={(e) => setName(e.target.value)} />
                        <WeightSelectControl
                            mode={UpsertAction.Update}
                            value={weight}
                            onChangeHandler={(e) => setWeight(Number.parseInt(e.target.value))} />
                    </Stack>
                </BusySpinner>
            </DialogContent>
            <StickyDialogActions phoneLayout='stacked' viewportOverride={viewportMode}>
                <Button onClick={() => setOpen(false)} variant='text' color='inherit'>Cancel</Button>
                <Button disabled={disableSave} onClick={handleEdit} variant='outlined' color='primary'>Submit</Button>
            </StickyDialogActions>

        </ResponsiveDialog>
    </>;
}

export default EditProficiencyGroupPopup;
